import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

import { useRelationRole } from 'hooks/useRelationRole';
import { useBusinessRole } from 'hooks/useBusinessRole';
import { useCaseUserClientDetails } from 'hooks/useCaseUserClientDetails';
import { generateFullName } from 'utils/generateFullNameUtils';
import { PI_PROVIDERS_TYPE } from 'constants/index';
import { useCaseContact } from 'modules/cases/hooks/useCaseContact';
import { useToggle } from 'hooks/useToggle';
import { formatPhoneNumber, getTenantDetails, formatDateWithoutTimezone } from 'utils/utils';
import ImageComponent from 'shared/ImageComponent';
import AddContact from './AddContact';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import { tableCell } from 'modules/reports/components/TableCell/TableCell';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import InputSwitch from 'components/common/PrimeReactInputSwitch/PrimeReactInputSwitch';
import LawftIdSelect from 'components/UI/LawftIdSelect/LawftIdSelect';
import { resolveLawftIdOptions } from 'hooks/queries/useLawftIdDropdown';
import { getExcludedLawftIdsForInsurer, getLawftIdDisplayLabels, mergeLawftIdLabelCache } from 'utils/lawftIdListHelpers';
import { updateContactDetails } from 'services/contact';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { updateCaseContactV2 } from 'services/CaseServices';
import { listInsurers, deleteInsurer, createInsurer, updateInsurer } from 'services/insuranceProviderService';
import AddInsurerDialog from 'components/AddInsurerDialog/AddInsurerDialog';
import { parseDOB } from 'modules/lead/helpers/helpers';
import GlobalTooltip from 'components/GlobalTooltip';

const LAWFT_ID_MENU_STYLES = {
  menu: (base) => ({ ...base, zIndex: 9999, minWidth: '280px' }),
  multiValue: (base) => ({ ...base, maxWidth: '160px', minWidth: '60px' }),
  multiValueLabel: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
};

const LawftIdCellBody = React.memo(function LawftIdCellBody({
  rowData,
  rowIndex,
  selectedIds,
  labelCacheRef,
  practiceAreaFilters,
  practiceAreaId,
  excludeLawftIds,
  onSave,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editOptions, setEditOptions] = useState([]);
  const originalIdsRef = useRef([]);

  const serverNames = rowData?._lawft_id_name_ || [];
  const labels = serverNames.length ? serverNames : getLawftIdDisplayLabels(selectedIds || [], labelCacheRef.current);
  const lawftIds = labels.join(', ');

  const handleOpen = () => {
    const ids = selectedIds || [];
    originalIdsRef.current = [...ids];
    if (serverNames.length && ids.length === serverNames.length) {
      const patch = {};
      ids.forEach((id, i) => {
        if (id) patch[id] = serverNames[i];
      });
      labelCacheRef.current = { ...labelCacheRef.current, ...patch };
    }
    setEditOptions(
      resolveLawftIdOptions(
        ids,
        Object.entries(labelCacheRef.current).map(([value, label]) => ({ value, label }))
      )
    );
    setIsEditing(true);
  };

  const handleChange = (selected) => {
    labelCacheRef.current = mergeLawftIdLabelCache(labelCacheRef.current, selected || []);
    setEditOptions(selected || []);
  };

  const handleOptionsLoaded = useCallback(
    (opts) => {
      labelCacheRef.current = mergeLawftIdLabelCache(labelCacheRef.current, opts);
    },
    [labelCacheRef]
  );

  const handleMenuClose = () => {
    const currentIds = editOptions.map((opt) => opt.value);
    const original = originalIdsRef.current;
    const hasChanged = currentIds.length !== original.length || currentIds.some((id, i) => id !== original[i]);
    setIsEditing(false);
    if (hasChanged) {
      onSave(currentIds);
    }
  };

  return (
    <>
      <span className="p-column-title text-break">Lawft ID</span>
      {isEditing ? (
        <div className="input-shadow w-100">
          <LawftIdSelect
            value={editOptions}
            onChange={handleChange}
            onMenuClose={handleMenuClose}
            autoFocus
            defaultMenuIsOpen
            placeholder="Select Lawft ID"
            className="input-shadow w-100"
            status="ACTIVE"
            filters={practiceAreaFilters}
            practice_area={practiceAreaFilters?.practice_area}
            excludeLawftIds={excludeLawftIds}
            isDisabled={!practiceAreaId}
            styles={LAWFT_ID_MENU_STYLES}
            onOptionsLoaded={handleOptionsLoaded}
          />
        </div>
      ) : (
        <>
          <span
            className={`text-break lawft-id_${rowData?.contact_type}_${rowIndex} ellipsis-text`}
            style={{ cursor: 'pointer' }}
            onClick={handleOpen}
          >
            {labels?.length ? lawftIds : '-'}
          </span>
          <Tooltip target={`.lawft-id_${rowData?.contact_type}_${rowIndex}`} content={lawftIds} position="top" />
        </>
      )}
    </>
  );
});

export default function RelatedContacts({ isLeadSpecific, caseDetails, getDetails }) {
  const history = useHistory();
  const [isOpenAddContact, toggleAddContact] = useToggle();
  const [isOpenAddBusiness, toggleAddBusiness] = useToggle();
  const [isOpenAddLocation, toggleAddLocation] = useToggle();
  const [iconIndex, setIconIndex] = useState([]);
  const [selectedLawftIdList, setSelectedLawftIdList] = useState({});

  const practiceAreaId = caseDetails?.case_description?.case_practice_area;
  const practiceAreaFilters = useMemo(
    () => ({
      practice_area: practiceAreaId ? [`${practiceAreaId}`] : [],
      show_unassigned_pa_column: true,
    }),
    [practiceAreaId]
  );

  const lawftIdLabelCacheRef = useRef({});
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showAddInsurerDialog, setShowAddInsurerDialog] = useState(false);
  const [editingInsurer, setEditingInsurer] = useState(null);
  const { relationRoleList } = useRelationRole(caseDetails?.case_description?.case_practice_area);
  const { businessRoleList } = useBusinessRole(caseDetails?.case_description?.case_practice_area);
  const { caseUserClientList, loadCaseUserClientDetails } = useCaseUserClientDetails();
  const { updateRelations, updateContact, deleteRelationMember, addContactRelation } = useCaseContact({
    caseDetails,
    isLeadSpecific,
    getDetails,
  });

  const addContactRef = useRef(null);
  const addBusinessRef = useRef(null);
  const addLocationRef = useRef(null);
  const { addToast } = useToast();
  const { control, setValue } = useForm();

  function getOtherValues(obj, excludeKey) {
    return Object.entries(obj)
      .filter(([key]) => key !== excludeKey)
      .flatMap(([_, values]) => values);
  }

  const openAddContact = () => {
    toggleAddContact();
    setTimeout(() => {
      if (addContactRef.current) {
        addContactRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const openAddBusiness = () => {
    toggleAddBusiness();
    setTimeout(() => {
      if (addBusinessRef.current) {
        addBusinessRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const openAddLocation = () => {
    toggleAddLocation();
    setTimeout(() => {
      if (addLocationRef.current) {
        addLocationRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const personContacts = useMemo(() => {
    const clientContacts = caseDetails?.client_details.reduce((accumulator, currentValue) => {
      if (currentValue?.is_primary_contact !== true && caseDetails?.client_details?.length !== 1) {
        accumulator.push({
          ...currentValue,
          is_manually_added: true,
        });
      }
      return accumulator;
    }, []);

    const clientDetail = caseDetails?.client_details?.[0];
    if (clientDetail) {
      const { email } = clientDetail;

      if (!clientContacts.some((obj) => obj.email === email) && clientDetail.contact_type === 'person') {
        clientDetail._role_ = 'Client';
        clientContacts.push(clientDetail);
      }
    }

    return [
      ...clientContacts,
      ...caseDetails?.relations?.filter((data) => !PI_PROVIDERS_TYPE.includes(data?.contact_type) && data?.contact_type === 'person'),
    ];
  }, [caseDetails]);

  const businessContacts = useMemo(() => {
    const clientContacts = caseDetails?.client_details.reduce((accumulator, currentValue) => {
      if (currentValue?.is_primary_contact !== true && caseDetails?.client_details?.length !== 1) {
        accumulator.push({
          ...currentValue,
          is_manually_added: true,
        });
      }
      return accumulator;
    }, []);

    const clientDetail = caseDetails?.client_details?.[0];
    if (clientDetail) {
      const { email } = clientDetail;

      if (!clientContacts.some((obj) => obj.email === email) && clientDetail.contact_type === 'business') {
        clientDetail._role_ = 'Client';
        clientContacts.push(clientDetail);
      }
    }
    return [
      ...clientContacts,
      ...caseDetails?.relations?.filter((data) => !PI_PROVIDERS_TYPE.includes(data?.contact_type) && data?.contact_type === 'business'),
    ];
  }, [caseDetails]);

  const locationContacts = useMemo(() => {
    const clientContacts = caseDetails?.client_details.reduce((accumulator, currentValue) => {
      if (currentValue?.is_primary_contact !== true && caseDetails?.client_details?.length !== 1) {
        accumulator.push({
          ...currentValue,
          is_manually_added: true,
        });
      }
      return accumulator;
    }, []);

    const clientDetail = caseDetails?.client_details?.[0];
    if (clientDetail) {
      const { email } = clientDetail;

      if (!clientContacts.some((obj) => obj.email === email) && clientDetail.contact_type === 'location') {
        clientDetail._role_ = 'Client';
        clientContacts.push(clientDetail);
      }
    }

    return [
      ...clientContacts,
      ...caseDetails?.relations?.filter((data) => !PI_PROVIDERS_TYPE.includes(data?.contact_type) && data?.contact_type === 'location'),
    ];
  }, [caseDetails]);

  const clientDetails = useMemo(() => {
    if (caseDetails?.client_details?.length > 0) {
      return caseDetails?.client_details[0];
    } else {
      return caseDetails?.client_details?.find((data) => data?.is_primary_contact === true);
    }
  }, [caseDetails]);

  const clientDetail = caseDetails?.client_details?.[0];
  if (clientDetail) {
    const { contact_type, email } = clientDetail;
    const targetArray = contact_type === 'person' ? personContacts : businessContacts;

    if (!targetArray.some((obj) => obj.email === email)) {
      targetArray.push(clientDetails);
    }
  }

  const piContacts = useMemo(() => caseDetails?.relations?.filter((data) => PI_PROVIDERS_TYPE.includes(data?.contact_type)), [caseDetails]);

  const onContactSubmit = (formData, isNew, toggle) => {
    addContactRelation(isNew, formData).then(() => {
      toggle === 'person' ? toggleAddContact() : toggle === 'business' ? toggleAddBusiness() : toggleAddLocation();
    });
  };

  const confirmDeleteMember = (data) => {
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === data?.contact_id;
    confirmDialog({
      message: `Are you sure want to delete this ${isClientContact ? 'client' : 'relation'}?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteRelationMember(data?.contact_id),
    });
  };

  // -----------------Related contacts columns Start----------------------
  const relatedName = (rowData) => {
    const { sk, pk, contact_id, title, first_name, profile_image, contact_type } = rowData;
    const route = contact_id ? `/contacts/${contact_id}` : '/contacts';
    return (
      <>
        <span className="p-column-title text-break">Name</span>
        <div className="d-flex align-items-center">
          <div className="px-0">
            <ImageComponent filePath={profile_image} fileName={contact_type === 'business' ? title : first_name} fileSize="small.jpg" />
          </div>

          <p className="px-0 d-flex flex-column contact-dynamic-ellipsis-container">
            <Link
              to={route}
              state={rowData}
              onClick={() => {
                handleSettingSkAndPkToLocalStorage(sk, pk);
              }}
              style={{ textDecoration: 'none' }}
            >
              <div
                className="redirection-link-no-text-transform contact-dynamic-ellipsis-text"
                title={generateFullName(rowData)}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {generateFullName(rowData)}
              </div>
            </Link>

            {!(rowData?.status === 'ACTIVE') ? (
              <Tag className="tag-cases border-0 archived-tags" value={rowData?.status} rounded></Tag>
            ) : null}
          </p>
        </div>
      </>
    );
  };

  const getLeadRoleName = (rowData, rowOption) => {
    if (rowData.contact_type === 'business') {
      const selectedOption = rowOption.filter((option) => option?.business_role_id === rowData?.role);
      return selectedOption?.[0]?.name || '';
    } else if (rowData.contact_type === 'person') {
      const selectedOption = rowOption.filter((option) => option?.relation_role_id === rowData?.role);
      return selectedOption?.[0]?.relation_role || '';
    }
  };

  const relatedRole = (rowData) => {
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
    const rowOption = rowData?.contact_type === 'business' ? businessRoleList : relationRoleList;
    return (
      <>
        <span className="p-column-title text-break">Role</span>
        <span>{isClientContact ? rowData?._role_ : isLeadSpecific ? getLeadRoleName(rowData, rowOption) : rowData?._role_ ?? '-'}</span>
      </>
    );
  };

  const relatedDOB = (rowData, props) => {
    const age = rowData?.dob ? ageCalculation(rowData.dob) : null;
    return (
      <>
        <span className="p-column-title text-break">DOB</span>
        <span className={`dob-${props?.rowIndex}`} style={{ cursor: 'pointer' }}>
          {rowData?.dob ? formatDateWithoutTimezone(rowData.dob) : '-'}

          <Tooltip target={`.dob-${props?.rowIndex}`} content={age && `Age: ${age}`} position="top" />
        </span>
      </>
    );
  };

  const toggleVisibility = (rowIndex) => {
    setIconIndex((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleSSN = (ssn) => {
    if (!ssn) return '-';
    let last = ssn.slice(-3);
    return `***-**-${last}`;
  };

  const getExcludeLawftIdsForContact = useCallback(
    (contactId) => {
      const contactLawftIds = getOtherValues(selectedLawftIdList, contactId);
      const insurerLawftIds = insuranceProviders.flatMap((insurer) => insurer.lawft_id || []);
      return [...contactLawftIds, ...insurerLawftIds];
    },
    [selectedLawftIdList, insuranceProviders]
  );

  const relatedSSN = (rowData, { rowIndex }) => {
    const isVisible = iconIndex[rowIndex] || false;
    const hasSSN = rowData?.ssn !== undefined && rowData?.ssn !== '' && rowData?.ssn !== null;
    const displayValue = isVisible ? rowData?.ssn : handleSSN(rowData?.ssn);

    return (
      <>
        <span className="p-column-title text-break">SSN</span>
        <div className="d-flex align-items-center w-100 min-w-0">
          <span
            className="ellipsis-text min-w-0 flex-grow-0 flex-shrink-1"
            style={{ cursor: 'pointer' }}
            title={hasSSN ? String(rowData.ssn) : undefined}
          >
            {displayValue}
          </span>
          {hasSSN && (
            <span
              role="button"
              tabIndex={0}
              className="flex-shrink-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleVisibility(rowIndex);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleVisibility(rowIndex);
              }}
            >
              {isVisible ? <i className="pi pi-eye-slash px-3"></i> : <i className="pi pi-eye px-3"></i>}
            </span>
          )}
        </div>
      </>
    );
  };

  const relatedAddress = (rowData, props) => {
    const addressText = `Street : ${rowData.street ?? ''}
        City : ${rowData.city ?? ''}
        State : ${rowData.state ?? ''}
        Zip : ${rowData.zip ?? ''}`;

    return (
      <>
        <span className="p-column-title text-break">Address</span>
        <div className={`d-flex flex-column address_${rowData?.contact_type}_${props?.rowIndex}`}>
          {!rowData.street && !rowData.city && !rowData.state && !rowData.zip ? (
            '-'
          ) : (
            <>
              <span className="ellipsis-text">{rowData.street ?? ''}</span>
              <span className="ellipsis-text">{rowData.city ?? ''}</span>
              <span className="ellipsis-text">{rowData.state ?? ''}</span>
              <span className="ellipsis-text">{rowData.zip ?? ''}</span>
            </>
          )}
          <Tooltip
            target={`.address_${rowData?.contact_type}_${props?.rowIndex}`}
            content={(rowData.street || rowData.city || rowData.state || rowData.zip) && addressText}
            position="top"
          />
        </div>
      </>
    );
  };

  const relatedType = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Type</span>
        <span>{rowData.business_type ?? '-'}</span>
      </>
    );
  };

  const relatedContactType = (rowData) => {
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
    const handleClickPrimary = async (e) => {
      if (rowData?.is_primary_contact !== true) {
        if (!!rowData?.is_manually_added || isClientContact) {
          delete rowData._role_;
          updateContact({ ...rowData, [e.target.name]: e.checked }, rowData?.contact_id, true);
          await getDetails();
        } else {
          updateRelations({ relations: [{ ...rowData, [e.target.name]: e.checked }] });
        }
      }
    };

    const handleClickbilling = async (e) => {
      if (isClientContact) {
        delete rowData._role_;
        updateContact({ ...rowData, [e.target.name]: e.checked }, rowData?.contact_id, true);
        await getDetails();
      } else {
        updateRelations({ relations: [{ ...rowData, [e.target.name]: e.checked }] });
      }
    };

    return (
      <>
        <span className="p-column-title text-break">Contact Type</span>
        <div className="d-flex flex-column">
          <span className="d-inline-table">
            <Checkbox
              checked={rowData.is_primary_contact}
              name="is_primary_contact"
              onChange={handleClickPrimary}
              disabled={!(rowData?.status === 'ACTIVE')}
            />
            <span className="px-2">Primary</span>
          </span>
          <span className="d-inline-table">
            <Checkbox
              checked={rowData.is_billing_contact}
              name="is_billing_contact"
              onChange={handleClickbilling}
              disabled={!(rowData?.status === 'ACTIVE')}
            />
            <span className="px-2">Billing</span>
          </span>
        </div>
      </>
    );
  };

  const relatedPrimaryContact = (rowData) => {
    const handleClick = (e) => {
      if (!!rowData?.is_manually_added) {
        updateContact({ ...rowData, [e.target.name]: e.checked }, rowData?.contact_id, true);
      } else {
        updateRelations({ relations: [{ ...rowData, [e.target.name]: e.checked }] });
      }
    };
    return (
      <>
        <span className="p-column-title text-break">Primary Contact</span>
        <span className="d-inline-table">
          <Checkbox
            checked={rowData.is_primary_contact}
            name="is_primary_contact"
            onChange={handleClick}
            disabled={!(rowData?.status === 'ACTIVE')}
          />
        </span>
      </>
    );
  };

  const relatedPrimaryContactName = (rowData, props) => {
    return (
      <>
        <span className="p-column-title text-break">Primary Contact</span>
        <span className={`primary-contact-name-${rowData?.contact_type}-${props?.rowIndex}`}>
          {' '}
          {rowData?.primary_contact_name ? rowData?.primary_contact_name : '-'}
        </span>
        <Tooltip
          target={`.primary-contact-name-${rowData?.contact_type}-${props?.rowIndex}`}
          content={rowData?.primary_contact_name && `${rowData?.primary_contact_name}`}
          position="top"
        />
      </>
    );
  };

  const onBehalfTemplate = (rowData) => {
    const isLeadContact = caseDetails?.main_client?.[0]?.contact_id === rowData?.contact_id;
    const handleClick = (e) => {
      updateRelations({ relations: [{ ...rowData, type: e.checked ? 'on-behalf' : undefined }] });
    };
    return (
      <>
        <span className="p-column-title text-break">On Behalf</span>
        <span className="d-inline-table">
          {!isLeadContact ? (
            <Checkbox
              checked={rowData.type === 'on-behalf'}
              name="is_primary_contact"
              onChange={handleClick}
              disabled={rowData.type === 'on-behalf' || rowData?.status !== 'ACTIVE'}
              className={rowData.type === 'on-behalf' || rowData?.status !== 'ACTIVE' ? 'p-disabled' : ''}
            />
          ) : (
            '-'
          )}
        </span>
      </>
    );
  };

  const relatedBillingContact = (rowData) => {
    const handleClick = (e) => updateRelations({ relations: [{ ...rowData, [e.target.name]: e.checked }] });
    return (
      <>
        <span className="p-column-title text-break">Billing Contact</span>
        <span className="d-inline-table">
          <Checkbox
            checked={rowData.is_billing_contact}
            name="is_billing_contact"
            onChange={handleClick}
            disabled={!(rowData?.status === 'ACTIVE')}
          />
        </span>
      </>
    );
  };

  const relatedLawftAccess = (rowData, props) => {
    let isEmailExist = Boolean(rowData?.email);
    let statusArchived = rowData?.status === 'ARCHIVED';
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
    const handleClick = async (e) => {
      let _item = { ...rowData };
      if (e.value === false) {
        _item.notify_case_updates = false;
      }
      _item['sk'] = `contact#${_item?.contact_id}`;
      const updateData = { relations: [{ ..._item, [e.target.name]: e.value }] };
      if (isClientContact) {
        delete _item._role_;
        await updateContactDetails({ ..._item, [e.target.name]: e.value })
          .then(() => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CONTACT_UPDATE_SUCCESS);
          })
          .catch(() => {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CONTACT_UPDATE_FAILED);
          });
        await getDetails();
      } else {
        await updateRelations(updateData);
      }
    };
    return (
      <>
        <span className="p-column-title text-break black-700">Enable Lawft Access</span>
        <span className="d-inline-table">
          <GlobalTooltip tooltip={!isEmailExist ? 'Add Email to Enable Lawft Access' : ''} position="top">
            <InputSwitch
              checked={rowData.is_create_client_portal}
              onChange={handleClick}
              disabled={!isEmailExist || rowData?.contact_type === 'business' || statusArchived}
              name="is_create_client_portal"
            />
          </GlobalTooltip>
        </span>
      </>
    );
  };

  const relatedAction = (rowData, props) => {
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
    const handleClick = () => {
      if (rowData?.status === 'ACTIVE') {
        confirmDeleteMember(rowData);
      }
    };
    const disabled =
      rowData?.type === 'on-behalf' || rowData?.status === 'ARCHIVED' || isClientContact || rowData?.is_primary_contact === true;
    return (
      <>
        <span className="p-column-title text-break">Action</span>
        <span className={'d-inline-table'}>
          <i
            className={'icon-delete cursor-pointer icon-red ' + (disabled ? 'opacity-50' : '')}
            onClick={!disabled ? handleClick : undefined}
            id={`delete-icon-${rowData?.contact_id}-${props?.rowIndex}`}
          />
        </span>
        {isClientContact ? (
          <Tooltip target={`#delete-icon-${rowData?.contact_id}-${props?.rowIndex}`} content="Client shouldn't be deleted" position="top" />
        ) : rowData?.is_primary_contact === true ? (
          <Tooltip
            target={`#delete-icon-${rowData?.contact_id}-${props?.rowIndex}`}
            content="Primary contact shouldn't be deleted"
            position="top"
          />
        ) : rowData?.type === 'on-behalf' ? (
          <Tooltip
            target={`#delete-icon-${rowData?.contact_id}-${props?.rowIndex}`}
            content="On behalf contact shouldn't be deleted"
            position="top"
          />
        ) : null}
      </>
    );
  };

  const roleEditor = ({ rowData }) => {
    const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
    const updatedRelationList = relationRoleList.filter((option) => option?.relation_role !== 'Client');

    const handleChange = (e) =>
      updateRelations({ relations: [{ ...rowData, [e.target.name]: e.value, client_id: clientDetails?.contact_id }] });

    return (
      <React.Fragment>
        {rowData?.status === 'ACTIVE' && !isClientContact ? (
          <Dropdown
            value={rowData?.role}
            onChange={handleChange}
            name="role"
            options={rowData?.contact_type === 'business' ? businessRoleList : updatedRelationList}
            optionLabel={rowData?.contact_type === 'business' ? 'name' : 'relation_role'}
            optionValue={rowData?.contact_type === 'business' ? 'business_role_id' : 'relation_role_id'}
            placeholder="Select"
            className="input-shadow w-100"
          />
        ) : (
          <>{isClientContact ? rowData?._role_ : rowData?.role ?? '-'}</>
        )}
      </React.Fragment>
    );
  };

  const relatedPhn = (rowData, props) => {
    return (
      <>
        <span className="p-column-title text-break">Phone Number</span>
        <span className={`phone-${props?.rowIndex}`}>{formatPhoneNumber(rowData?.phone) ?? '-'}</span>
        <Tooltip
          target={`.phone-${props?.rowIndex}`}
          content={rowData?.additional_phone && `Alternative Phone : ${rowData?.additional_phone}`}
          position="top"
        />
      </>
    );
  };

  const handleSSNEditor = ({ rowData, rowIndex }) => {
    const handleSSNUpdate = async (value, rowData) => {
      const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
      if (isClientContact) {
        delete rowData._role_;
        let sk = `contact#${rowData?.contact_id}`;
        await updateContactDetails({ ...rowData, ssn: value, tenantId: rowData?.tenantId, ssn_flag: true, sk })
          .then(() => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CONTACT_UPDATE_SUCCESS);
          })
          .catch(() => {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CONTACT_UPDATE_FAILED);
          });
        await getDetails();
      } else {
        updateRelations({ relations: [{ ...rowData, ssn: value, tenantId: rowData?.tenantId, ssn_flag: true }] });
      }
      setValue(`${rowData?.ssn}_${rowIndex}`, rowData?.ssn);
    };
    return (
      <div className="input-shadow w-100">
        <Controller
          name={`${rowData?.ssn}_${rowIndex}`}
          control={control}
          render={({ field }) => (
            <InputText
              type="text"
              className="input-shadow w-100"
              placeholder="Enter SSN"
              value={field.value ?? rowData?.ssn ?? ''}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value) && e.target.value.length <= 9) {
                  field.onChange(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                handleSSNUpdate(e.target.value, rowData);
              }}
              onBlur={(e) => {
                handleSSNUpdate(e.target.value, rowData);
              }}
            />
          )}
        />
      </div>
    );
  };

  const primaryContactEditor = ({ rowData, rowIndex }) => {
    const handlePrimaryContactUpdate = async (data) => {
      const isClientContact = caseDetails?.client_details?.[0]?.contact_id === rowData?.contact_id;
      const [tenantId] = getTenantDetails();
      const sk = `contact#${rowData?.contact_id}`;
      const pk = `${tenantId}#${rowData?.contact_id}`;
      if (isClientContact) {
        delete rowData._role_;
      }
      try {
        await updateContactDetails({ ...rowData, primary_contact_name: data, tenantId: tenantId, sk, pk });
        await getDetails();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CONTACT_UPDATE_SUCCESS);
      } catch (error) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CONTACT_UPDATE_FAILED);
      }
      setValue(`${rowData?.primary_contact_name}_${rowIndex}`, rowData?.primary_contact_name);
    };
    return (
      <div className="input-shadow w-100">
        <Controller
          name={`primary_contact_${rowData?.contact_id}`}
          control={control}
          defaultValue={rowData?.primary_contact_name || ''}
          render={({ field }) => (
            <InputText
              {...field}
              type="text"
              className="input-shadow w-100"
              placeholder="Enter Primary Contact"
              onChange={(e) => field.onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                handlePrimaryContactUpdate(e.target.value);
              }}
              onBlur={(e) => {
                handlePrimaryContactUpdate(e.target.value);
              }}
            />
          )}
        />
      </div>
    );
  };

  const relatedPhoneAndFax = (rowData, props) => {
    return (
      <>
        <span className="p-column-title text-break">Phone Number & Fax</span>
        <div className={`d-flex flex-column phone_and_fax-${rowData?.contact_type}-${props?.rowIndex}`}>
          {!rowData?.phone && !rowData?.fax_number ? (
            '-'
          ) : (
            <>
              <span className={`ellipsis-text`}>{formatPhoneNumber(rowData?.phone) !== '' ? formatPhoneNumber(rowData?.phone) : '-'}</span>
              <span className={`ellipsis-text`}>{rowData?.fax_number ? rowData?.fax_number : '-'}</span>
            </>
          )}
        </div>
        <Tooltip
          target={`.phone_and_fax-${rowData?.contact_type}-${props?.rowIndex}`}
          content={rowData?.additional_phone && `Alternative Phone : ${rowData?.additional_phone}`}
          position="top"
        />
      </>
    );
  };

  const ageCalculation = (dob) => {
    if (!dob) return '-';
    const DOB = parseDOB(dob);
    if (!DOB) return '-';
    const today = new Date();

    const yearDiff = today.getFullYear() - DOB.getFullYear();
    return yearDiff;
  };

  const lawftIdCellBody = (rowData, props) => (
    <LawftIdCellBody
      rowData={rowData}
      rowIndex={props?.rowIndex}
      selectedIds={selectedLawftIdList[rowData?.contact_id]}
      labelCacheRef={lawftIdLabelCacheRef}
      practiceAreaFilters={practiceAreaFilters}
      practiceAreaId={practiceAreaId}
      excludeLawftIds={getExcludeLawftIdsForContact(rowData?.contact_id)}
      onSave={(ids) => {
        setSelectedLawftIdList((prev) => ({ ...prev, [rowData.contact_id]: ids }));
        handleUpdateLawftID(rowData, ids);
      }}
    />
  );

  const handleUpdateLawftID = async (rowData, selectedData) => {
    let sk = rowData?.sk;
    if (isLeadSpecific) {
      if (rowData?.contact_id === caseDetails?.main_client?.[0]?.contact_id) {
        delete rowData._role_;
        sk = `case_contact#${caseDetails?.client_details?.[0]?.case_contact_id}`;
      } else {
        sk = `case_relation#${rowData.case_relation_id}`;
      }
      rowData['sk'] = sk;
    }
    delete rowData._role_;
    await updateCaseContactV2(caseDetails?.case_id, { ...rowData, lawft_id: selectedData }, sk)
      .then(() => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_UPDATED_CREATED);
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CASE_UPDATED_FAILED);
      });
    await getDetails();
  };

  // Load insurance providers from caseDetails directly
  const loadInsuranceProviders = useCallback(() => {
    if (caseDetails?.insurance_details && Array.isArray(caseDetails.insurance_details)) {
      setInsuranceProviders(caseDetails?.insurance_details);
    } else {
      setInsuranceProviders([]);
    }
  }, [caseDetails?.insurance_details]);

  // Delete insurance provider
  const confirmDeleteInsurer = (data) => {
    confirmDialog({
      message: 'Are you sure want to delete this insurance provider?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const caseId = caseDetails?.case_id;
          await deleteInsurer(data?.pk, data?.sk);
          addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider deleted successfully');
          // Reload case details to get updated insurance providers
          await getDetails();
        } catch (error) {
          addToast(false, toastConstant.toasterType.ERROR, 'Error', 'Failed to delete insurance provider');
        }
      },
    });
  };

  // Handle Add/Edit Insurer Submit
  const onSubmitInsurer = async (data) => {
    try {
      // Determine if insured person is client or defendant
      const insuredContactId = data.insured_person;
      const isClient = caseDetails?.client_details?.some((client) => client.contact_id === insuredContactId);
      const isDefendant = caseDetails?.defendant?.some((defendant) => defendant.contact_id === insuredContactId);

      // Map form data to API structure
      const insurerData = {
        case_id: caseDetails?.case_id,
        insurer_name: data.insurance_company,
        insured_person: {
          contact_id: insuredContactId, // Pass as object with contact_id
        },
        contact_name: data.contact_name,
        email: data.email,
        phone_number: data.phone,
        additional_phone: data.additional_phone || '',
        fax: data.fax || '',
        policy_number: data.policy_number,
        claim_number: data.claim_number || '',
        insurance_limit: parseFloat(data.insurance_limit) || 0,
        lawft_id: data.lawft_id || [], // Add lawft_id array
        client_insurer: isClient, // Flag as client insurer if insured person is client
        defendant_insurer: isDefendant, // Flag as defendant insurer if insured person is defendant
      };

      let response;
      if (editingInsurer) {
        // Update existing insurer
        const updateData = {
          ...insurerData,
          insurer_pk: editingInsurer.pk,
          insurer_sk: editingInsurer.sk,
        };
        response = await updateInsurer(updateData);

        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider updated successfully');
      } else {
        // Call the create insurer API
        response = await createInsurer(insurerData);
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider added successfully');
      }

      if (response?.data?.insurer_id || response?.data?.message || response) {
        // Reload case details to get updated insurance providers
        await getDetails();
        setShowAddInsurerDialog(false);
        setEditingInsurer(null);
      }
    } catch (error) {
      console.error('Error saving insurer:', error);
      addToast(
        false,
        toastConstant.toasterType.ERROR,
        'Error',
        error?.response?.data?.body || `Failed to ${editingInsurer ? 'update' : 'add'} insurance provider`
      );
    }
  };

  // Update insurer helper function
  const handleUpdateInsurer = async (rowData, updates) => {
    try {
      // If insured_person is being updated, recalculate the client_insurer and defendant_insurer flags
      let additionalUpdates = {};
      if (updates.insured_person) {
        const insuredContactId = updates.insured_person?.contact_id || updates.insured_person;
        const isClient = caseDetails?.client_details?.some((client) => client.contact_id === insuredContactId);
        const isDefendant = caseDetails?.defendant?.some((defendant) => defendant.contact_id === insuredContactId);

        additionalUpdates = {
          insured_person: {
            contact_id: insuredContactId, // Pass as object with contact_id
          },
          client_insurer: isClient,
          defendant_insurer: isDefendant,
        };
      }

      const updateData = {
        ...rowData,
        ...updates,
        ...additionalUpdates,
        insurer_pk: rowData.pk,
        insurer_sk: rowData.sk,
      };

      await updateInsurer(updateData);

      // Also update the corresponding contact object fields if this is a client or defendant insurer
      // This ensures bidirectional sync between insurance_details table and contact fields
      // Use updated contact_id if insured_person was changed, otherwise use the existing one
      // Handle both object and string formats
      const insuredContactId = updates.insured_person
        ? typeof updates.insured_person === 'string'
          ? updates.insured_person
          : updates.insured_person?.contact_id
        : typeof rowData?.insured_person === 'string'
        ? rowData.insured_person
        : rowData?.insured_person?.contact_id;
      if (insuredContactId) {
        // Find if this is a client
        const clientMatch = caseDetails?.client_details?.find((client) => client.contact_id === insuredContactId);
        if (clientMatch) {
          const contactUpdates = {
            ...clientMatch,
            insurer: updates.insurer_name !== undefined ? updates.insurer_name : rowData.insurer_name,
            policy_number: updates.policy_number !== undefined ? updates.policy_number : rowData.policy_number,
            claim_number: updates.claim_number !== undefined ? updates.claim_number : rowData.claim_number,
            insurance_limit: updates.insurance_limit !== undefined ? updates.insurance_limit : rowData.insurance_limit,
          };
          await updateCaseContactV2(caseDetails?.case_id, contactUpdates, clientMatch.sk);
        }

        // Find if this is a defendant
        const defendantMatch = caseDetails?.defendant?.find((defendant) => defendant.contact_id === insuredContactId);
        if (defendantMatch) {
          const contactUpdates = {
            ...defendantMatch,
            insurer: updates.insurer_name !== undefined ? updates.insurer_name : rowData.insurer_name,
            policy_number: updates.policy_number !== undefined ? updates.policy_number : rowData.policy_number,
            claim_number: updates.claim_number !== undefined ? updates.claim_number : rowData.claim_number,
            insurance_limit: updates.insurance_limit !== undefined ? updates.insurance_limit : rowData.insurance_limit,
          };
          await updateCaseContactV2(caseDetails?.case_id, contactUpdates, defendantMatch.sk);
        }
      }

      addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider updated successfully');

      // Reload case details to get updated insurance providers
      await getDetails();
    } catch (error) {
      console.error('Error updating insurer:', error);
      addToast(false, toastConstant.toasterType.ERROR, 'Error', 'Failed to update insurance provider');
    }
  };

  // -----------------Insurance Provider columns Start----------------------
  const insurerName = (rowData) => {
    const handleInsurerNameClick = () => {
      // Navigate to the Negotiations tab (tab_id=3 in expenses)
      const negotiationsPath = `/cases/${caseDetails?.case_id}/expenses?tab_id=3`;
      history.push(negotiationsPath);
    };

    return (
      <>
        <span className="p-column-title text-break">Insurance Company</span>
        <span className="redirection-link" onClick={handleInsurerNameClick} style={{ cursor: 'pointer' }}>
          {rowData?.insurer_name || '-'}
        </span>
      </>
    );
  };

  const insurerContact = (rowData, props) => {
    const contactName = rowData?.contact_name || '-';
    return (
      <>
        <span className="p-column-title text-break">Contact</span>
        <span className={`insurer-contact-${props?.rowIndex}`}>{contactName}</span>
        {contactName !== '-' && <Tooltip target={`.insurer-contact-${props?.rowIndex}`} content={contactName} position="top" />}
      </>
    );
  };

  const insurerInsures = (rowData, props) => {
    // Display the insured person nam

    let displayName = '';
    let contactData = null;

    // Handle both cases: insured_person as object or as contact_id string
    const contactId = typeof rowData?.insured_person === 'string' ? rowData.insured_person : rowData?.insured_person?.contact_id;

    // Prioritize insured_person object data if it has name information
    if (rowData?.insured_person && typeof rowData.insured_person === 'object') {
      contactData = rowData.insured_person;

      // Check if first_name exists to determine if it's a person or business
      if (contactData.first_name) {
        // Person: use title + first_name + middle_name + last_name
        displayName = generateFullName(contactData);
      } else if (contactData.title) {
        // Business: use only title
        displayName = contactData.title;
      }
    }

    // If no display name yet, try to find from caseUserClientList as fallback
    if (!displayName) {
      const matchedContact = caseUserClientList.find((contact) => contact.contact_id === contactId);

      if (matchedContact) {
        contactData = matchedContact;

        if (matchedContact.first_name) {
          // Person: use title + first_name + middle_name + last_name
          displayName = generateFullName(matchedContact);
        } else if (matchedContact.title) {
          // Business: use only title
          displayName = matchedContact.title;
        }
      }
    }

    const fullName = displayName || '-';
    const route = contactId ? `/contacts/${contactId}` : null;

    return (
      <>
        <span className="p-column-title text-break">Insurers</span>
        {route && fullName !== '-' ? (
          <Link
            to={route}
            onClick={() => {
              // Use contactData if available
              const sk = contactData?.sk;
              const pk = contactData?.pk;
              if (sk && pk) {
                handleSettingSkAndPkToLocalStorage(sk, pk);
              }
            }}
            className="redirection-link text-decoration-none"
          >
            <span className={`insurer-insures-${props?.rowIndex}`}>{fullName}</span>
          </Link>
        ) : (
          <span className={`insurer-insures-${props?.rowIndex}`}>{fullName}</span>
        )}
        {fullName !== '-' && <Tooltip target={`.insurer-insures-${props?.rowIndex}`} content={fullName} position="top" />}
      </>
    );
  };

  const insurerEmail = (rowData, props) => {
    const email = rowData?.email || '-';
    return (
      <>
        <span className="p-column-title text-break">Email</span>
        <span className={`insurer-email-${props?.rowIndex}`}>{email}</span>
        {email !== '-' && <Tooltip target={`.insurer-email-${props?.rowIndex}`} content={email} position="top" />}
      </>
    );
  };

  const insurerPhone = (rowData, props) => {
    return (
      <>
        <span className="p-column-title text-break">Phone</span>
        <span className={`insurer-phone-${props?.rowIndex}`}>{formatPhoneNumber(rowData?.phone_number) || '-'}</span>
        <Tooltip
          target={`.insurer-phone-${props?.rowIndex}`}
          content={rowData?.additional_phone && `Alternative Phone : ${formatPhoneNumber(rowData?.additional_phone)}`}
          position="top"
        />
      </>
    );
  };

  const insurerFax = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Fax</span>
        <span>{rowData?.fax || '-'}</span>
      </>
    );
  };

  const insurerPolicyNo = (rowData, props) => {
    const policyNumber = rowData?.policy_number || '-';
    return (
      <>
        <span className="p-column-title text-break">Policy No</span>
        <span className={`insurer-policy-${props?.rowIndex}`}>{policyNumber}</span>
        {policyNumber !== '-' && <Tooltip target={`.insurer-policy-${props?.rowIndex}`} content={policyNumber} position="top" />}
      </>
    );
  };

  const insurerClaimNo = (rowData, props) => {
    const claimNumber = rowData?.claim_number || '-';
    return (
      <>
        <span className="p-column-title text-break">Claim No</span>
        <span className={`insurer-claim-${props?.rowIndex}`}>{claimNumber}</span>
        {claimNumber !== '-' && <Tooltip target={`.insurer-claim-${props?.rowIndex}`} content={claimNumber} position="top" />}
      </>
    );
  };

  const insurerLawftID = (rowData, props) => {
    const nameList = Array.isArray(rowData?._lawft_id_name_) ? rowData._lawft_id_name_ : [];
    const selectedIds = nameList.length ? nameList : getLawftIdDisplayLabels(rowData?.lawft_id, lawftIdLabelCacheRef.current);
    const lawftIds = selectedIds.join(', ');

    return (
      <>
        <span className="p-column-title text-break">Lawft ID</span>
        <span className={`text-break insurer-lawft-id_${props?.rowIndex} ellipsis-text`} style={{ cursor: 'pointer' }}>
          {selectedIds?.length ? lawftIds : '-'}
        </span>
        <Tooltip target={`.insurer-lawft-id_${props?.rowIndex}`} content={lawftIds || '-'} position="top" />
      </>
    );
  };

  const insurerAction = (rowData) => {
    const handleEditClick = () => {
      setEditingInsurer(rowData);
      setShowAddInsurerDialog(true);
    };

    return (
      <>
        <span className="p-column-title text-break">Action</span>
        <span className="d-inline-table">
          <i className="icon-edit cursor-pointer icon-blue me-3" onClick={handleEditClick} />
          <i className="icon-delete cursor-pointer icon-red" onClick={() => confirmDeleteInsurer(rowData)} />
        </span>
      </>
    );
  };
  // -----------------Insurance Provider columns End----------------------

  useEffect(() => {
    if (!caseDetails?.case_id) return;

    const selectedLawftId = {};
    caseDetails?.relations?.map((relation) => {
      selectedLawftId[relation?.contact_id] = relation.lawft_id;
    });
    caseDetails?.client_details
      ?.map((clientDetails) => {
        if (clientDetails.lawft_id) {
          selectedLawftId[clientDetails?.contact_id] = clientDetails.lawft_id;
        }
      })
      .filter(Boolean);
    caseDetails?.defendant
      ?.map((defendant) => {
        if (defendant.lawft_id) {
          selectedLawftId[defendant?.contact_id] = defendant.lawft_id;
        }
      })
      .filter(Boolean);
    setSelectedLawftIdList(selectedLawftId);

    // Load insurance providers from caseDetails
    loadInsuranceProviders();

    // Load case contacts for insurance provider "Insures" dropdown (including person and business)
    loadCaseUserClientDetails(
      caseDetails.case_id,
      true, // is_contact_list
      false, // is_team_member_list
      true, // all_contacts - fetch all contact types (person, business, location)
      false, // contact_id
      false, // is_lead
      false, // is_mandatory_lawft_access
      false, // is_active
      false, // is_include_groups
      false // active_groups
    );
  }, [caseDetails?.case_id, caseDetails?.relations, caseDetails?.client_details, caseDetails?.defendant, loadInsuranceProviders]);

  return (
    <div className="row">
      <div className="col-sm-12">
        <Tooltip target=".info-icon-wrapper" />
        <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
          <div className="caption-bold mb-0">Person</div>
          {!isOpenAddContact ? (
            <div className="d-flex ">
              <div className="">
                <Button className="p-button p-button-secondary" onClick={openAddContact}>
                  Add Contact
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="datatable-responsive">
          <ResizableDataTable
            value={personContacts}
            dataKey="contact_id"
            responsiveLayout="scroll"
            emptyMessage="N/A"
            className="p-datatable-responsive related-contacts"
            stripedRows
            columns={[
              { field: 'first_name', header: 'Name', body: relatedName, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: '_role_', header: 'Role', body: relatedRole, sortable: true, bodyClassName: 'ellipsis-text', editor: roleEditor },
              { field: 'phone', header: 'Phone Number', body: relatedPhn, sortable: false, bodyClassName: 'ellipsis-text' },
              { field: 'email', header: 'Email', body: tableCell, sortable: false, bodyClassName: 'ellipsis-text' },
              { field: 'dob', header: 'DOB', body: relatedDOB, sortable: true, bodyClassName: 'ellipsis-text' },
              {
                field: 'lawft_ID',
                header: 'Lawft ID',
                body: lawftIdCellBody,
                sortable: false,
                bodyClassName: 'ellipsis-text',
              },
              { field: 'ssn', header: 'SSN', body: relatedSSN, sortable: false, bodyClassName: 'ellipsis-text', editor: handleSSNEditor },
              {
                field: 'type',
                header: (
                  <span style={{ fontWeight: 'normal' }} className="tooltip-header d-flex align-items-center">
                    On Behalf
                    <span className="ms-1 info-icon-wrapper" data-pr-tooltip="Person who made first contact" data-pr-position="top">
                      <i className="pi pi-info-circle on-behalfs" style={{ fontSize: '13px', pointerEvents: 'none' }} />
                    </span>
                  </span>
                ),
                body: onBehalfTemplate,
                sortable: true,
                bodyClassName: 'ellipsis-text',
                hidden: !isLeadSpecific,
              },

              {
                field: 'is_contact_type',
                header: 'Contact Type',
                body: relatedContactType,
                sortable: false,
                bodyClassName: 'ellipsis-text',
                hidden: isLeadSpecific,
              },
              { field: 'is_create_client_portal', header: 'Lawft Access', body: relatedLawftAccess, bodyClassName: 'ellipsis-text' },
              {
                field: 'non_pi_action_icons',
                header: 'Action',
                body: relatedAction,
                bodyClassName: 'ellipsis-text related-contact-action',
              },
            ].filter((val) => val)}
            storageKey="case_contacts_nonpi_column_widths"
          />
        </div>
        {isOpenAddContact && (
          <div ref={addContactRef} className="add-contact-section">
            <AddContact
              {...{
                toggleAddContact,
                isLeadSpecific,
                onContactSubmit,
                toggle: 'person',
                practice_area: caseDetails?.case_description?.case_practice_area,
              }}
            />
          </div>
        )}
      </div>

      <div className="col-12 mt-3">
        <Tooltip target=".info-icon-wrapper" />
        <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
          <div className="caption-bold mb-0">Business</div>
          {!isOpenAddBusiness ? (
            <div className="d-flex ">
              <div className="">
                <Button className="p-button p-button-secondary" onClick={openAddBusiness}>
                  Add Business
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="datatable-responsive">
          <ResizableDataTable
            value={businessContacts}
            dataKey="contact_id"
            responsiveLayout="scroll"
            emptyMessage="N/A"
            className="p-datatable-responsive related-contacts"
            stripedRows
            columns={[
              { field: 'title', header: 'Name', body: relatedName, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: 'business_type', header: 'Type', body: relatedType, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: '_role_', header: 'Role', body: relatedRole, sortable: false, bodyClassName: 'ellipsis-text', editor: roleEditor },
              {
                field: 'is_primary_contact',
                header: 'Primary Contact',
                body: relatedPrimaryContactName,
                sortable: false,
                bodyClassName: 'ellipsis-text',
                // hidden: isLeadSpecific,
                editor: primaryContactEditor,
              },
              { field: 'email', header: 'Email', body: tableCell, sortable: false, bodyClassName: 'ellipsis-text' },
              {
                field: 'phone',
                header: 'Phone Number & Fax',
                body: relatedPhoneAndFax,
                sortable: false,
                bodyClassName: 'ellipsis-text',
              },
              {
                field: 'lawft_ID',
                header: 'Lawft ID',
                body: lawftIdCellBody,
                sortable: false,
                bodyClassName: 'ellipsis-text',
              },
              { field: 'address', header: 'Address', body: relatedAddress, sortable: false, bodyClassName: 'ellipsis-text' },
              {
                field: 'non_pi_action_icons',
                header: 'Action',
                body: relatedAction,
                bodyClassName: 'ellipsis-text related-contact-action',
              },
            ].filter((val) => val)}
            storageKey="case_contacts_nonpi_column_widths"
          />
        </div>
        {isOpenAddBusiness && (
          <div ref={addBusinessRef} className="add-business-section">
            <AddContact
              {...{
                toggleAddBusiness,
                isLeadSpecific,
                onContactSubmit,
                toggle: 'business',
                practice_area: caseDetails?.case_description?.case_practice_area,
              }}
            />
          </div>
        )}
      </div>

      <div className="col-12 mt-3 mb-4">
        <Tooltip target=".info-icon-wrapper" />
        <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
          <div className="caption-bold mb-0">Location</div>
          {!isOpenAddLocation ? (
            <div className="d-flex ">
              <div className="">
                <Button className="p-button p-button-secondary" onClick={openAddLocation}>
                  Add Location
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="datatable-responsive">
          <ResizableDataTable
            value={locationContacts}
            responsiveLayout="scroll"
            emptyMessage="N/A"
            className="p-datatable-responsive related-contacts"
            stripedRows
            columns={[
              { field: 'title', header: 'Name', body: relatedName, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: 'address', header: 'Address', body: relatedAddress, sortable: false, bodyClassName: 'ellipsis-text' },
              {
                field: 'is_primary_contact',
                header: 'Primary Contact',
                body: relatedPrimaryContactName,
                sortable: false,
                bodyClassName: 'ellipsis-text',
                // hidden: isLeadSpecific,
                editor: primaryContactEditor,
              },
              {
                field: 'phone',
                header: 'Phone Number & Fax',
                body: relatedPhoneAndFax,
                sortable: false,
                bodyClassName: 'ellipsis-text',
              },
              { field: 'email', header: 'Email', body: tableCell, sortable: false, bodyClassName: 'ellipsis-text' },
              {
                field: 'non_pi_action_icons',
                header: 'Action',
                body: relatedAction,
                bodyClassName: 'ellipsis-text related-contact-action',
              },
            ].filter((val) => val)}
            storageKey="case_contacts_nonpi_column_widths"
          />
        </div>
        {isOpenAddLocation && (
          <div ref={addLocationRef} className="add-contact-section">
            <AddContact {...{ toggleAddLocation, isLeadSpecific, onContactSubmit, toggle: 'location' }} />
          </div>
        )}
      </div>

      {/* Insurance Providers Section */}
      {caseDetails?.case_description?._case_practice_area_?.toLowerCase() === 'personal injury' && !caseDetails?.is_lead && (
        <div className="col-sm-12 mt-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="caption-bold mb-0">INSURANCE PROVIDERS</div>
            <div className="d-flex">
              <Button className="p-button p-button-secondary" onClick={() => setShowAddInsurerDialog(true)}>
                Add Insurance Provider
              </Button>
            </div>
          </div>
          <div className="datatable-responsive">
            <ResizableDataTable
              value={insuranceProviders}
              responsiveLayout="scroll"
              emptyMessage="No insurance providers added"
              className="p-datatable-responsive related-contacts"
              stripedRows
              columns={[
                {
                  field: 'insurer_name',
                  header: 'Insurance Company',
                  body: insurerName,
                  sortable: true,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'contact_person',
                  header: 'Contact',
                  body: insurerContact,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'insures',
                  header: 'Insurers',
                  body: insurerInsures,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'email',
                  header: 'Email',
                  body: insurerEmail,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'phone_number',
                  header: 'Phone',
                  body: insurerPhone,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'fax',
                  header: 'Fax',
                  body: insurerFax,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'policy_number',
                  header: 'Policy No',
                  body: insurerPolicyNo,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'claim_no',
                  header: 'Claim No',
                  body: insurerClaimNo,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                {
                  field: 'lawft_id',
                  header: 'Lawft ID',
                  body: insurerLawftID,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                },
                { field: 'action', header: 'Action', body: insurerAction, bodyClassName: 'ellipsis-text related-contact-action' },
              ]}
              storageKey="case_insurance_providers_column_widths_v2"
            />
          </div>
        </div>
      )}

      {!!piContacts.length && (
        <div className="col-12 mt-3">
          {/* <Card title="Providers and Vendors"> */}
          <div className="contact-card-title mb-4">Providers and Vendors</div>
          <ResizableDataTable
            value={piContacts}
            responsiveLayout="scroll"
            emptyMessage="N/A"
            className="p-datatable-responsive related-contacts"
            stripedRows
            columns={[
              { field: 'first_name', header: 'Name', body: relatedName, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: 'email', header: 'Email', body: tableCell, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: 'phone', header: 'Phone Number', body: relatedPhn, sortable: true, bodyClassName: 'ellipsis-text' },
              { field: 'role', header: 'Role', body: relatedRole, sortable: true, bodyClassName: 'ellipsis-text', editor: roleEditor },
              {
                field: 'is_primary_contact',
                header: 'Primary Contact',
                body: relatedPrimaryContact,
                sortable: true,
                bodyClassName: 'ellipsis-text',
                hidden: isLeadSpecific,
              },
              {
                field: 'is_billing_contact',
                header: 'Billing Contact',
                body: relatedBillingContact,
                sortable: true,
                bodyClassName: 'ellipsis-text',
                hidden: isLeadSpecific,
              },
              { field: 'pi_action_icons', header: 'Action', body: relatedAction, bodyClassName: 'ellipsis-text related-contact-action' },
            ].filter((val) => val)}
            storageKey="case_contacts_pi_column_widths"
          />
          {/* </Card> */}
        </div>
      )}

      {/* Add/Edit Insurance Dialog */}
      <AddInsurerDialog
        visible={showAddInsurerDialog}
        onHide={() => {
          setShowAddInsurerDialog(false);
          setEditingInsurer(null);
        }}
        onSubmit={onSubmitInsurer}
        showInsurerDropdown={true}
        caseId={caseDetails?.case_id}
        caseDetails={caseDetails}
        insuranceProviders={insuranceProviders}
        onInsuranceAdded={() => loadInsuranceProviders()}
        excludeLawftIds={getExcludedLawftIdsForInsurer({
          selectedLawftIdList,
          insuranceProviders,
          currentInsurerPk: editingInsurer?.pk,
        })}
        editMode={!!editingInsurer}
        initialData={
          editingInsurer
            ? {
                insured_person:
                  typeof editingInsurer.insured_person === 'string'
                    ? editingInsurer.insured_person
                    : editingInsurer.insured_person?.contact_id,
                insurance_company: editingInsurer.insurer_name,
                contact_name: editingInsurer.contact_name,
                email: editingInsurer.email,
                phone: editingInsurer.phone_number,
                additional_phone: editingInsurer.additional_phone,
                fax: editingInsurer.fax,
                policy_number: editingInsurer.policy_number,
                claim_number: editingInsurer.claim_number,
                insurance_limit: editingInsurer.insurance_limit,
                lawft_id: editingInsurer.lawft_id || [],
                lawft_id_names: editingInsurer._lawft_id_name_ || [],
              }
            : null
        }
        requiredFields={[
          'insured_person',
          'insurance_company',
          'contact_name',
          'email',
          'phone',
          'fax',
          'policy_number',
          'claim_number',
          'insurance_limit',
        ]}
      />
    </div>
  );
}
